import React, { useRef, useState, useEffect } from 'react';
import type { Table, Reservation, FloorFixture } from '../../types';

const FLOOR_WIDTH = 1200;
const FLOOR_HEIGHT = 800;

interface TablesReservationStatusProps {
  tables: Table[];
  reservations: Reservation[];
  /** Show bookings for this date only */
  selectedDate: string;
  /** Optional floor layout (bar, walls, etc.) so table positions match the real floor */
  layoutComponents?: FloorFixture[];
}

/**
 * Table cards laid out in floor-plan format: same positions as the physical floor
 * so waitstaff can quickly see where each table is. Each card shows table number,
 * seats, and if booked: customer name and party size. Scales to fit on screen.
 */
const TablesReservationStatus: React.FC<TablesReservationStatusProps> = ({
  tables,
  reservations,
  selectedDate,
  layoutComponents = [],
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const fitScale = Math.min(w / FLOOR_WIDTH, h / FLOOR_HEIGHT, 1);
      // Higher min scale = bigger tables, less empty space; overflow is scrollable
      const s = Math.max(0.6, fitScale);
      setScale(s);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const bookingsForDate = reservations.filter(
    (r) =>
      r.reservationDate === selectedDate &&
      (r.status === 'pending' || r.status === 'confirmed')
  );

  const getBookingsForTable = (tableId: number) =>
    bookingsForDate
      .filter(
        (r) =>
          r.tableId === tableId ||
          (r.assignedTableIds != null && r.assignedTableIds.includes(tableId))
      )
      .sort((a, b) => a.reservationTime.localeCompare(b.reservationTime));

  // Show only primary table when merged (same as floor plan)
  const tablesToShow = tables.filter((table) => {
    const isMerged = table.mergedWith && table.mergedWith.length > 1;
    const primaryId = table.mergedWith?.[0];
    const isPrimary = !isMerged || primaryId == null || Number(primaryId) === table.id;
    return isPrimary;
  });

  const fixtureClasses: Record<string, string> = {
    entrance: 'bg-gray-200 border border-gray-300 text-gray-600',
    bar: 'bg-yellow-800 text-white font-bold',
    patio: 'bg-green-100 border-2 border-dashed border-green-400 text-green-700',
    kitchen: 'bg-orange-100 border-2 border-orange-400 text-orange-700',
    restroom: 'bg-blue-100 border-2 border-blue-400 text-blue-700',
    storage: 'bg-gray-300 border-2 border-gray-500 text-gray-700',
    counter: 'bg-amber-100 border-2 border-amber-400 text-amber-700',
    window: 'bg-sky-100 border-2 border-sky-400 text-sky-700',
    wall: 'bg-gray-400 border border-gray-600 text-gray-800',
    custom: 'bg-purple-100 border border-purple-300 text-purple-700',
  };

  return (
    <div className="mb-6 bg-white rounded-lg shadow overflow-hidden">
      <div className="px-3 py-1.5 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">
          Tables — {selectedDate}
        </h2>
        <p className="text-[11px] text-gray-500">
          Laid out like the floor plan. Booked = amber; free = gray.
        </p>
      </div>
      {/* Floor plan: fixed-height scrollable area so full floor is visible */}
      <div
        ref={wrapperRef}
        className="w-full overflow-auto bg-gray-100 rounded-b-lg"
        style={{ height: 'clamp(280px, 52vh, 500px)' }}
      >
        {/* Content sized to scaled floor so wrapper shows scrollbars when needed */}
        <div
          className="relative overflow-hidden bg-gray-100 rounded-b-lg"
          style={{
            width: FLOOR_WIDTH * scale,
            height: FLOOR_HEIGHT * scale,
            minWidth: FLOOR_WIDTH * scale,
            minHeight: FLOOR_HEIGHT * scale,
          }}
        >
          <div
            className="absolute top-0 left-0 bg-gray-100 origin-top-left"
            style={{
              width: FLOOR_WIDTH,
              height: FLOOR_HEIGHT,
              transform: `scale(${scale})`,
            }}
          >
        {/* Walls */}
        <div className="absolute top-0 left-0 h-full w-3 bg-gray-300" title="Wall" />
        <div className="absolute top-0 right-0 h-full w-3 bg-gray-300" title="Wall" />
        <div className="absolute bottom-0 left-0 w-full h-3 bg-gray-300" title="Wall" />

        {/* Layout fixtures (bar, kitchen, etc.) */}
        {layoutComponents.map((comp) => {
          const w = comp.width ?? 10;
          const h = comp.height ?? 10;
          const x = typeof comp.x === 'number' && !isNaN(comp.x) ? comp.x : 50;
          const y = typeof comp.y === 'number' && !isNaN(comp.y) ? comp.y : 50;
          const cls = fixtureClasses[comp.type] || fixtureClasses.custom;
          return (
            <div
              key={comp.id}
              className={`absolute text-center flex items-center justify-center text-xs font-semibold select-none ${cls}`}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: `${w}%`,
                height: `${h}%`,
                transform: `translate(-50%, -50%)${comp.rotation ? ` rotate(${comp.rotation}deg)` : ''}`,
              }}
            >
              <span className="truncate px-1">{comp.label}</span>
            </div>
          );
        })}

        {/* Table cards at floor plan positions */}
        {tablesToShow.map((table) => {
          const bookings = getBookingsForTable(table.id);
          const isBooked = bookings.length > 0;
          const first = bookings[0];
          const x = typeof table.x === 'number' && !isNaN(table.x) ? table.x : 50;
          const y = typeof table.y === 'number' && !isNaN(table.y) ? table.y : 50;

          return (
            <div
              key={table.id}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 rounded-lg border-2 p-2.5 min-w-[160px] max-w-[200px] shadow leading-tight ${
                isBooked
                  ? 'border-amber-400 bg-amber-50'
                  : 'border-gray-300 bg-gray-50'
              }`}
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className="font-semibold text-gray-800 text-base">
                Table {table.name}
              </div>
              <div className="text-sm text-gray-600">
                {table.capacity} seat{table.capacity !== 1 ? 's' : ''}
              </div>
              {isBooked ? (
                <div className="mt-1.5 text-sm">
                  <div
                    className="font-medium text-amber-900 truncate"
                    title={first?.customerName}
                  >
                    {first?.customerName ?? '—'}
                  </div>
                  <div className="text-amber-800 text-xs">
                    {first?.partySize} pax
                    {first?.reservationTime ? ` · ${first.reservationTime}` : ''}
                  </div>
                  {bookings.length > 1 && (
                    <div className="text-xs text-amber-700">
                      +{bookings.length - 1} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1 text-sm text-gray-500">Free</div>
              )}
            </div>
          );
        })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TablesReservationStatus;
